import { Decimal } from 'decimal.js';

// TODO: Create a package for this, so there is no duplication between frontend and backend.
// Until then, keep this in sync with api/src/utils/bids.ts.

interface TotalAndDiscountValues {
  totalValue: number | Decimal | null;
  discountRate: number | Decimal | null;
  absoluteDiscount: number | Decimal | null;
}

export interface PriceDetailsInput {
  absoluteDiscount: number | null;
  additionalCosts: number | null;
  discountRate: number | null;
  skontoRate: number | null;
  taxRate: number | null;
  totalNet: number | null;
}

export interface PriceDetails {
  absoluteDiscount: number;
  additionalCosts: number;
  discountRate: number;
  skontoRate: number;
  skontoValue: number;
  taxRate: number;
  taxValue: number;
  totalGross: number;
  totalGrossWithSkonto: number;
  totalNet: number;
  totalNetWithDiscount: number;
}

export const getDiscountValue = ({
  totalValue: totalValueProp,
  discountRate: discountRateProp,
  absoluteDiscount: absoluteDiscountProp,
}: TotalAndDiscountValues) => {
  const totalValue = new Decimal(totalValueProp ?? 0).toDecimalPlaces(2);
  const discountRate = new Decimal(discountRateProp ?? 0).toDecimalPlaces(4);
  const absoluteDiscount = new Decimal(absoluteDiscountProp ?? 0).toDecimalPlaces(2);

  if (!discountRate.equals(0)) {
    // To be consistent with GAEB, the total has to be multiplied with 1 - discount rate, and then
    // rounded to 2 decimal places.
    const totalWithDiscount = totalValue
      .times(new Decimal(1).minus(new Decimal(discountRate)))
      .toDecimalPlaces(2);
    return totalValue.minus(new Decimal(totalWithDiscount)).toNumber();
  }

  if (!absoluteDiscount.equals(0)) {
    return absoluteDiscount.toNumber();
  }

  return 0;
};

export const getDiscountPercent = ({
  totalValue: totalValueProp,
  discountRate: discountRateProp,
  absoluteDiscount: absoluteDiscountProp,
}: TotalAndDiscountValues) => {
  const totalValue = new Decimal(totalValueProp ?? 0).toDecimalPlaces(2);
  const discountRate = new Decimal(discountRateProp ?? 0).toDecimalPlaces(4);
  const absoluteDiscount = new Decimal(absoluteDiscountProp ?? 0).toDecimalPlaces(2);

  if (!discountRate.equals(0)) {
    return new Decimal(discountRate).toNumber();
  }

  if (!absoluteDiscount.equals(0) && !totalValue.equals(0)) {
    return new Decimal(absoluteDiscount).dividedBy(totalValue).toDecimalPlaces(4).toNumber();
  }

  return 0;
};

export const getBidPriceDetails = ({
  absoluteDiscount: absoluteDiscountProp,
  additionalCosts: additionalCostsProp,
  discountRate: discountRateProp,
  skontoRate: skontoRateProp,
  taxRate: taxRateProp,
  totalNet: totalNetProp,
}: PriceDetailsInput): PriceDetails => {
  const absoluteDiscount = new Decimal(absoluteDiscountProp ?? 0).toDecimalPlaces(2);
  const additionalCosts = new Decimal(additionalCostsProp ?? 0).toDecimalPlaces(2);
  const discountRate = new Decimal(discountRateProp ?? 0).toDecimalPlaces(4);
  const skontoRate = new Decimal(skontoRateProp ?? 0).toDecimalPlaces(4);
  const taxRate = new Decimal(taxRateProp ?? 0).toDecimalPlaces(4);
  const totalNet = new Decimal(totalNetProp ?? 0).toDecimalPlaces(2);

  const derivedDiscountRate = getDiscountPercent({
    totalValue: totalNet,
    discountRate,
    absoluteDiscount,
  });

  const derivedAbsoluteDiscount = getDiscountValue({
    totalValue: totalNet,
    discountRate,
    absoluteDiscount,
  });

  const totalNetWithDiscount = totalNet.minus(derivedAbsoluteDiscount).plus(additionalCosts);

  const taxValue = totalNetWithDiscount.times(taxRate).toDecimalPlaces(2);
  const totalGross = totalNetWithDiscount.plus(taxValue);

  const skontoValue = getDiscountValue({
    totalValue: totalGross,
    discountRate: skontoRate,
    absoluteDiscount: null,
  });

  const totalGrossWithSkonto = totalGross.minus(skontoValue);

  return {
    absoluteDiscount: derivedAbsoluteDiscount,
    additionalCosts: additionalCosts.toNumber(),
    discountRate: derivedDiscountRate,
    skontoRate: skontoRate.toNumber(),
    skontoValue,
    taxRate: taxRate.toNumber(),
    taxValue: taxValue.toNumber(),
    totalGross: totalGross.toNumber(),
    totalGrossWithSkonto: totalGrossWithSkonto.toNumber(),
    totalNet: totalNet.toNumber(),
    totalNetWithDiscount: totalNetWithDiscount.toNumber(),
  };
};
